import type { AxiosResponse } from 'axios';
import { omit } from 'lodash';
import type { IAppointment, IWarehouse } from '@nova/core';

import axiosClient from '@/api/axiosClient';
import useRecaptcha, { RecaptchaTokenError } from '@/components/composables/useRecaptcha';

export type CheckinPayload = Record<string, unknown>;

export type CompanyAutocompleteItem = {
  id: string;
  name: string;
  scac?: string;
  [key: string]: unknown;
};

export type CompanyAutocompleteResponse = {
  items: CompanyAutocompleteItem[];
  meta: { totalRecords: number; page: number };
};

export type CompanyAutocompletePayload = {
  search: string;
  pagination: { limit: number; offset: number };
};

export type UploadDocumentsParams = {
  warehouseId: string;
  appointmentIdentifier?: string;
  files: File[];
};

export type UploadDocumentResponse = { url: string; key: string };

export type RequestOptions = { signal?: AbortSignal };

function unwrap<T>(res: AxiosResponse): T {
  return (res.data?.data ?? res.data) as T;
}

export default function cometApiService() {
  const { getToken } = useRecaptcha();

  /**
   * Fail open: a token we could not generate must never block the driver. The request still goes
   * out, carrying an empty token plus the reason, which the backend logs as `empty-token` instead
   * of the indistinguishable `no-token`.
   */
  async function recaptchaHeaders(action: string): Promise<Record<string, string>> {
    try {
      return { 'X-Recaptcha-Token': await getToken(action) };
    } catch (err) {
      const reason = err instanceof RecaptchaTokenError ? err.reason : 'execute-failed';
      return { 'X-Recaptcha-Token': '', 'X-Recaptcha-Token-Error': reason };
    }
  }

  return {
    async getWarehouse(warehouseId: string): Promise<Partial<IWarehouse>> {
      const headers = await recaptchaHeaders('comet_initial');
      const res = await axiosClient.get(`/bff/comet/${warehouseId}/initial`, { headers });
      return unwrap(res);
    },

    async companyAutocomplete(
      warehouseId: string,
      payload: CompanyAutocompletePayload,
      options?: RequestOptions
    ): Promise<CompanyAutocompleteResponse> {
      const headers = await recaptchaHeaders('comet_company_autocomplete');
      const res = await axiosClient.post(
        `/bff/comet/${warehouseId}/company-autocomplete`,
        payload,
        {
          headers,
          signal: options?.signal
        }
      );
      return res.data;
    },

    async getAppointment(warehouseId: string, identifier: string): Promise<IAppointment> {
      const encoded = encodeURIComponent(identifier);
      const headers = await recaptchaHeaders('comet_appointment_lookup');
      const res = await axiosClient.get(`/bff/comet/${warehouseId}/appointment/${encoded}`, {
        headers
      });
      return unwrap(res);
    },

    async submitPlanned<T = CheckinPayload>(
      warehouseId: string,
      payload: CheckinPayload
    ): Promise<T> {
      const body = {
        ...omit(payload, ['warehouseId']),
        assetDetails: omit((payload.assetDetails ?? {}) as Record<string, unknown>, [
          'companyId',
          'companyHint',
          'driverNotes'
        ])
      };
      const headers = await recaptchaHeaders('comet_checkin_planned');
      const res = await axiosClient.put(`/bff/comet/${warehouseId}`, body, { headers });
      return unwrap(res);
    },

    async submitUnplanned<T = CheckinPayload>(
      warehouseId: string,
      payload: CheckinPayload
    ): Promise<T> {
      const body = omit(payload, ['warehouseId', 'appointmentId', 'notes']);
      const headers = await recaptchaHeaders('comet_checkin_unplanned');
      const res = await axiosClient.put(`/bff/comet/${warehouseId}/unplanned`, body, { headers });
      return unwrap(res);
    },

    async rejectCheckin<T = CheckinPayload>(
      warehouseId: string,
      payload: CheckinPayload
    ): Promise<T> {
      const headers = await recaptchaHeaders('comet_checkin_rejected');
      const res = await axiosClient.put(`/bff/comet/${warehouseId}/reject`, payload, { headers });
      return unwrap(res);
    },

    /**
     * Uploads the whole batch a multidoc field holds in one request. Sending one request per
     * file spent the throttle window on the first photo and failed the rest (PL-519).
     *
     * This route is not behind the BFF, so it takes no recaptcha token.
     */
    async uploadDocuments({
      warehouseId,
      appointmentIdentifier,
      files
    }: UploadDocumentsParams): Promise<UploadDocumentResponse[]> {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const res = await axiosClient.post(
        `checkin/public/storage/files/${warehouseId}`,
        formData,
        ...(appointmentIdentifier ? [{ params: { appointmentIdentifier } }] : [])
      );

      return res.data;
    },

    async smartGateAutocomplete(
      warehouseId: string,
      options?: RequestOptions
    ): Promise<Record<string, unknown>[]> {
      const res = await axiosClient.get(`/observations/public/${warehouseId}/autocomplete`, {
        signal: options?.signal
      });
      return res.data ?? [];
    }
  };
}
